<template>
    <div class="map-wrap">
        <!-- CONTROLES ESQUERDA -->
        <a-card class="switcher left" size="small" :bordered="true">
            <div class="ctrl">
                <div class="lbl">Camada de Base</div>
                <a-select
                    class="w-full ellipsis-select"
                    v-model:value="leftBaseKey"
                    @change="applyBoth"
                    :options="leftBaseOptions"
                    :dropdownMatchSelectWidth="true"
                />
            </div>

            <div class="ctrl">
                <div class="lbl">Camada</div>
                <a-select
                    class="w-full ellipsis-select"
                    v-model:value="leftOverlayKey"
                    @change="applyBoth"
                    :options="overlayOptions"
                    allow-clear
                    placeholder="Nenhum"
                    :dropdownMatchSelectWidth="true"
                    show-search
                    :filterOption="filterOverlay"
                    optionFilterProp="label"
                    :virtual="true"
                />
            </div>
        </a-card>

        <!-- CONTROLES DIREITA -->
        <a-card class="switcher right" size="small" :bordered="true">
            <div class="ctrl">
                <div class="lbl">Camada de Base</div>
                <a-select
                    class="w-full ellipsis-select"
                    v-model:value="rightBaseKey"
                    @change="applyBoth"
                    :options="rightBaseOptions"
                    :dropdownMatchSelectWidth="true"
                />
            </div>

            <div class="ctrl">
                <div class="lbl">Camada</div>
                <a-select
                    class="w-full ellipsis-select"
                    v-model:value="rightOverlayKey"
                    @change="applyBoth"
                    :options="overlayOptions"
                    allow-clear
                    placeholder="Nenhum"
                    :dropdownMatchSelectWidth="true"
                    show-search
                    :filterOption="filterOverlay"
                    optionFilterProp="label"
                    :virtual="true"
                />
            </div>
        </a-card>

        <!-- MAPA -->
        <div id="map" class="map"></div>

        <!-- SPINNER ESQUERDO (cobre só a faixa da esquerda até o swipe) -->
        <a-spin
            :spinning="loadingLeftOverlay"
            tip="Carregando camada..."
            class="side-spinner left"
            :style="leftSpinnerStyle"
        />
        <!-- SPINNER DIREITO (cobre só a faixa da direita a partir do swipe) -->
        <a-spin
            :spinning="loadingRightOverlay"
            tip="Carregando camada..."
            class="side-spinner right"
            :style="rightSpinnerStyle"
        />
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'

import OlMap from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import { defaults as defaultControls } from 'ol/control'
import Swipe from 'ol-ext/control/Swipe'

import { baseLayers as baseLayersGroup } from '@/open-layers/baseLayers'
import {buildStyleFunctionFromBackend, createLayer, createThematicLayer} from '../../../open-layers/helpers'
import { getDefaultMapView } from '../../../open-layers/map-defaults.js'

// ---------- Estado ----------
const overlayDefs = ref([]) // [{ key, title, make: ()=>Promise<Layer> }]

const leftBaseKey = ref(null)
const rightBaseKey = ref(null)
const leftOverlayKey = ref(null)
const rightOverlayKey = ref(null)

const leftBaseOptions = ref([])
const rightBaseOptions = ref([])
const overlayOptions = computed(() =>
    overlayDefs.value.map(o => ({ label: o.title, value: o.key }))
)

const filterOverlay = (input, option) =>
    String(option?.label ?? '').toLowerCase().includes(String(input).toLowerCase())

let map, swipe
const layerPool = new Map() // cacheKey => Layer
const baseDefs = ref([])

const loadingLeftOverlay = ref(false)
const loadingRightOverlay = ref(false)

// posição do swipe (0..1)
const swipePos = ref(0.5)

// estilos dinâmicos dos spinners por lado
const leftSpinnerStyle = computed(() => ({
    left: 0,
    width: `${Math.max(0, Math.min(1, swipePos.value)) * 100}%`,
}))
const rightSpinnerStyle = computed(() => ({
    left: `${Math.max(0, Math.min(1, swipePos.value)) * 100}%`,
    width: `${(1 - Math.max(0, Math.min(1, swipePos.value))) * 100}%`,
}))

// ---------- Helpers ----------
function buildBaseDefsFromGroup (group) {
    const arr = group.getLayers().getArray()
    return arr
        .filter(l => l instanceof TileLayer)
        .map(l => {
            const title = l.get('title') || 'Base'
            const source = l.getSource()
            return {
                key: title,
                title,
                make: () => new TileLayer({ source, visible: true }),
            }
        })
}

function getCachedOrCreate (cacheKey, factory) {
    if (layerPool.has(cacheKey)) return layerPool.get(cacheKey)
    const layer = factory()
    layerPool.set(cacheKey, layer)
    return layer
}

function ensureInMap (layer) {
    if (!layer) return
    const layers = map.getLayers().getArray()
    if (!layers.includes(layer)) map.addLayer(layer)
    if (typeof layer.setVisible === 'function') layer.setVisible(true)
}

function removeFromSwipe (layer) {
    if (!layer || !swipe) return
    try { swipe.removeLayer(layer) } catch {}
}

function removeAllFromSwipeAndMap () {
    layerPool.forEach(l => {
        removeFromSwipe(l)
        try { map.removeLayer(l) } catch {}
        if (typeof l.setVisible === 'function') l.setVisible(false)
    })
}

// cria/recupera overlay sob demanda, ligando o spinner do lado
async function getOrBuildOverlay (def, side) {
    const cacheKey = `${def.key}::${side}`
    const isCached = layerPool.has(cacheKey)

    // liga o spinner somente se NÃO estiver em cache
    const flag = side === 'left' ? loadingLeftOverlay : loadingRightOverlay
    if (!isCached) flag.value = true
    try {
        if (isCached) return layerPool.get(cacheKey)
        const layer = await def.make()
        layerPool.set(cacheKey, layer)
        return layer
    } finally {
        if (!isCached) flag.value = false
    }
}

// ---------- Aplicação (esquerda/direita) ----------
async function applyBoth () {
    if (!swipe) return
    removeAllFromSwipeAndMap()

    // Bases
    const leftBaseDef = baseDefs.value.find(d => d.key === leftBaseKey.value)
    const leftBase = leftBaseDef
        ? getCachedOrCreate(`${leftBaseKey.value}::left`, leftBaseDef.make)
        : null

    const rightBaseDef = baseDefs.value.find(d => d.key === rightBaseKey.value)
    const rightBase = rightBaseDef
        ? getCachedOrCreate(`${rightBaseKey.value}::right`, rightBaseDef.make)
        : null

    // Overlays
    const leftOverlayDef = overlayDefs.value.find(d => d.key === leftOverlayKey.value)

    const sameOverlayBothSides =
        leftOverlayKey.value && rightOverlayKey.value &&
        leftOverlayKey.value === rightOverlayKey.value

    const rightOverlayDef = !sameOverlayBothSides
        ? overlayDefs.value.find(d => d.key === rightOverlayKey.value)
        : null


    let leftOverlay = null
    let rightOverlay = null

    // Carregar overlays por lado (spinners separados)
    if (leftOverlayDef) {
        leftOverlay = await getOrBuildOverlay(leftOverlayDef, 'left')
    }
    if (rightOverlayDef) {
        rightOverlay = await getOrBuildOverlay(rightOverlayDef, 'right')
    }

    // Adicionar no mapa + swipe (esquerda)
    if (leftBase)   { ensureInMap(leftBase);   swipe.addLayer(leftBase) }
    if (leftOverlay){ ensureInMap(leftOverlay);swipe.addLayer(leftOverlay) }

    // Direita
    if (rightBase)   { ensureInMap(rightBase);   swipe.addLayer(rightBase, true) }
    if (rightOverlay){ ensureInMap(rightOverlay);swipe.addLayer(rightOverlay, true) }
}

// ---------- Carrega definições (sem instanciar) ----------
const getAllLayers = async () => {
    const baseResp = await window.axios.get('api/maps/layers?onlyIsNotGroup&onlyAlloweds')
    const registeredLayers = baseResp?.data?.data ?? []

    const normalDefs = registeredLayers.map(dto => ({
        key: `layer-${dto.id}`,
        title: dto.name,
        make: async () => await createLayer(dto)
    }))

    const thematicResp = await window.axios.get('api/maps/thematic-layers?onlyAlloweds')
    const thematicDTOs = thematicResp?.data?.data ?? []

    const thematicDefs = thematicDTOs.map(dto => {
        dto.style = buildStyleFunctionFromBackend(dto)
        return {
            key: `thematic-${dto.id}`,
            title: dto.name,
            make: async () => await createThematicLayer(dto)
        }
    })

    overlayDefs.value = [...normalDefs, ...thematicDefs]
}

// ---------- Lifecycle ----------
onMounted(async () => {
    await getAllLayers()

    map = new OlMap({
        target: 'map',
        layers: [],
        view: new View(getDefaultMapView()),
        controls: defaultControls(),
    })

    const ctl = new Swipe({ position: 0.5 })
    swipe = ctl
    map.addControl(swipe)

    // acompanha a posição do swipe para dimensionar os spinners
    // (ol-ext emite "moving" com { position })
    swipe.on('moving', (e) => {
        if (typeof e.position === 'number') swipePos.value = e.position
    })

    // Bases
    const defs = buildBaseDefsFromGroup(baseLayersGroup)
    baseDefs.value = defs

    const asOpts = defs.map(d => ({ label: d.title, value: d.key }))
    leftBaseOptions.value = asOpts
    rightBaseOptions.value = asOpts

    const osmKey = defs.find(d => /open\s*street\s*map/i.test(d.title))?.key ?? defs[0]?.key
    leftBaseKey.value = osmKey
    rightBaseKey.value = osmKey

    // aplica individualmente (não limpa o outro lado)
    await applyBoth()
})
</script>

<style scoped>
.map-wrap { position: relative; width: 100%; height: 100vh; }
.map { width: 100%; height: 100%; }

.switcher {
    position: absolute;
    top: 12px;
    z-index: 1000;
    width: 300px;
    padding: 6px 8px;
}
.switcher.left { left: 12px; }
.switcher.right { right: 12px; }

.ctrl { display: flex; align-items: center; margin-bottom: 8px; gap: 8px; }
.lbl { width: 110px; font-size: 12px; color: #666; }

.w-full { width: 100%; }

.ellipsis-select :deep(.ant-select-selector) {
    max-width: 100%;
}
.ellipsis-select :deep(.ant-select-selection-item) {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Spinners por lado: ocupam só a faixa correspondente */
.side-spinner {
    position: absolute;
    top: 0;
    height: 100%;
    display: grid;
    place-items: center;
    pointer-events: none;
    z-index: 999; /* acima do mapa, abaixo dos cards */
}
.side-spinner.left { left: 0; }
.side-spinner.right { right: 0; }
</style>
